import * as Yup from 'yup';
import { phoneNumberValidation } from './yup-validation';
import EcodigoForm from './ecodigo-form';

declare global {
  interface Window {
    grecaptcha: unknown;
  }
}

export default (): void => {
  const grecaptcha = window.grecaptcha;
  const recaptchaToken = document.querySelector('meta[name="google-recaptcha"]')?.getAttribute('content');
  const contactForm = document.querySelector('.ecodigo-contact-form') as HTMLElement | null;
  const formElement = contactForm?.querySelector('form') as HTMLFormElement | null;

  if (!contactForm || !formElement) return;

  const fields = [
    'lastname',
    'firstname',
    'phone',
    'email',
    'message',
    'subject_particular',
    'subject_professional',
    'subject_collectivity',
    'subject_specific',
    'files',
  ];

  const schema = Yup.object().shape({
    customer_type: Yup.string(),
    lastname: Yup.string()
      .required('Le nom est requis')
      .min(2, 'Le nom doit contenir au moins 2 caractères')
      .max(50, 'Le nom ne peut pas dépasser 50 caractères'),
    firstname: Yup.string()
      .required('Le nom est requis')
      .min(2, 'Le nom doit contenir au moins 2 caractères')
      .max(50, 'Le nom ne peut pas dépasser 50 caractères'),
    phone: Yup.string().test(phoneNumberValidation).required('Le numéro de téléphone est requis'),
    email: Yup.string().required("L'email est requis").email("L'email n'est pas valide"),
    message: Yup.string()
      .required('Le message est requis')
      .min(10, 'Le message doit contenir au moins 10 caractères')
      .max(500, 'Le message ne peut pas dépasser 1000 caractères'),
    subject_particular: Yup.string().when('customer_type', {
      is: 'particular',
      then: (schema) => schema.required('Le sujet est requis'),
      otherwise: (schema) => schema.notRequired(),
    }),
    subject_professional: Yup.string().when('customer_type', {
      is: 'professional',
      then: (schema) => schema.required('Le sujet est requis'),
      otherwise: (schema) => schema.notRequired(),
    }),
    subject_collectivity: Yup.string().when('customer_type', {
      is: 'collectivity',
      then: (schema) => schema.required('Le sujet est requis'),
      otherwise: (schema) => schema.notRequired(),
    }),
    subject_specific: Yup.string().when(
      [
        'customer_type',
        'subject_particular',
        'subject_professional',
        'subject_collectivity',
        '$customer_type', // $ are usd for context values
        '$subject_particular', // $ are usd for context values
        '$subject_professional', // $ are usd for context values
        '$subject_collectivity', // $ are usd for context values
      ],
      {
        is: (
          customer_type: string | undefined,
          subject_particular: string | undefined,
          subject_professional: string | undefined,
          subject_collectivity: string | undefined,
          $customer_type: string | undefined,
          $subject_particular: string | undefined,
          $subject_professional: string | undefined,
          $subject_collectivity: string | undefined,
        ) => {
          customer_type = customer_type ?? $customer_type;
          subject_particular = subject_particular ?? $subject_particular;
          subject_professional = subject_professional ?? $subject_professional;
          subject_collectivity = subject_collectivity ?? $subject_collectivity;

          switch (customer_type) {
            case 'particular':
              return subject_particular === 'other';
            case 'professional':
              return subject_professional === 'other';
            case 'collectivity':
              return subject_collectivity === 'other';
            default:
              return false;
          }
        },
        then: (schema) => schema.required('Veuillez préciser votre sujet'),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    files: Yup.mixed().when(['subject_particular'], {
      is: ($subject_particular: string | undefined, subject_particular: string | undefined) => {
        subject_particular = subject_particular ?? $subject_particular;
        return subject_particular === 'Candidature spontanée';
      },
      then: (schema) =>
        schema.test({
          name: 'required-file',
          message: 'Veuillez sélectionner au moins un fichier',
          test: function (inputFiles) {
            const isSet = inputFiles && typeof inputFiles === 'object' && 'size' in inputFiles;

            if (isSet) {
              return (inputFiles as Set<File>).size > 0;
            }

            return false;
          },
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const form = new (class extends EcodigoForm {
    constructor() {
      if (!contactForm || !formElement) return;

      super(contactForm, formElement, fields, schema, grecaptcha, recaptchaToken, false);
      const originalInit = this.init;

      this.init = () => {
        originalInit.call(this);

        (contactForm.querySelectorAll('input[name=customer_type]') as NodeListOf<HTMLInputElement>).forEach((item) => {
          item.addEventListener('change', function () {
            form.displayCustomerTypeInputs(item.value);
          });
        });

        (contactForm.querySelectorAll('select[name^=subject_]') as NodeListOf<HTMLInputElement>).forEach((item) => {
          const specificSubject = contactForm?.querySelector('input[name=subject_specific]') as HTMLInputElement;
          item.addEventListener('change', function () {
            if (item.value === 'other') {
              specificSubject.removeAttribute('disabled');
            } else {
              specificSubject.setAttribute('disabled', 'disabled');
              specificSubject.value = '';
            }
          });
        });

        this.loadFromHash();

        window.addEventListener(
          'hashchange',
          () => {
            this.loadFromHash();
          },
          false,
        );
      };

      this.getDataObject = () => {
        const dataObject: { [key: string]: string | unknown } = {};
        this.fields.forEach((fieldName) => {
          const input = this.form.querySelector(`[name=${fieldName}]`) as HTMLInputElement;
          dataObject[fieldName] = input.value;
        });
        dataObject.customer_type = (
          this.form.querySelector('input[name=customer_type]:checked') as HTMLInputElement
        ).value;
        dataObject.files = this.fileUploader?.files ?? [];
        return dataObject;
      };

      this.submitForm = async (): Promise<boolean> => {
        this.triggerErrors = true;

        const formData = new FormData();
        const dataObject = this.getDataObject();

        fields.forEach((fieldName) => {
          const input = this.formWrapper?.querySelector(`[name=${fieldName}]`) as HTMLInputElement;
          const value = input.value;
          formData.append(fieldName, value);
        });

        formData.append('customer_type', dataObject.customer_type as string);

        this.fileUploader?.getFiles().forEach((file) => {
          formData.append('files[]', file);
        });

        // Reset errors
        fields.forEach((fieldName) => {
          this.showError(fieldName, '');
        });

        try {
          await schema.validate(dataObject, { abortEarly: false });

          const values = {
            recaptcha: undefined,
            ...formData,
          };

          // @ts-expect-error grecaptcha is defined
          await new Promise((resolve) => this.grecaptcha.ready(resolve));
          // @ts-expect-error grecaptcha is defined
          values.recaptcha = await this.grecaptcha.execute(recaptchaToken, { action: 'submitForm' });
          if (values.recaptcha) {
            formData.append('recaptcha', values.recaptcha);
          }

          const sendData = await fetch('/wp-json/ecodigo/contact_form', {
            method: 'POST',
            body: formData,
          });

          await this.handleSubmitResponse(sendData);
        } catch (err) {
          (err as Yup.ValidationError).inner.forEach((error: Yup.ValidationError) => {
            this.showError(error.path ?? '', error.message);
          });
        }

        return true;
      };
    }

    loadFromHash = (): string => {
      const hash = window.location.hash.replace('#', '');
      if (hash !== '') {
        this.form.classList.remove('d-none');
        contactForm?.querySelector('.ecodigo-form__selection')?.classList.add('d-none');
        (document.querySelector(`input[name=customer_type][value=${hash}]`) as HTMLInputElement).click();
      }
      return hash;
    };

    displayCustomerTypeInputs = (customerType: string): void => {
      window.location.hash = customerType;
      const customerTypeInputs = document.querySelectorAll(`[data-customer-type]`);
      customerTypeInputs.forEach((item) => {
        if ((item as HTMLElement).dataset.customerType === customerType) {
          item.classList.add('--is-active');
        } else {
          item.classList.remove('--is-active');
        }
      });

      // Reset subject fields
      (document.querySelectorAll('select[name^=subject_]') as NodeListOf<HTMLInputElement>).forEach((item) => {
        item.value = '';
      });

      // reset fields
      this.form.querySelector('[data-error="global"]')?.classList.add('d-none');
      fields.forEach((fieldName) => {
        const input = document.querySelector(`[name=${fieldName}]`) as HTMLInputElement;
        input.value = '';
        this.clearError(fieldName);
        this.triggerErrors = false;
      });
    };
  })();

  form.init();
};
